<template>
  <b-modal :id="modalId" size="lg" scrollable modal-class="db-modal" @show="alAbrir" @hidden="reiniciar">
    <template #modal-header>
      <div class="db-modal-head">
        <h2 class="db-modal-title">{{ usuario ? "Editar usuario" : "Nuevo usuario" }}</h2>
        <p class="db-modal-subtitle">
          <template v-if="usuario">Alta: {{ formatFecha(usuario.creado) }} · Último acceso: {{ formatFechaHora(usuario.ultimo_acceso) }}</template>
          <template v-else>Comunícale la contraseña inicial por un canal seguro.</template>
        </p>
      </div>
      <button type="button" class="db-icon-btn" aria-label="Cerrar" @click="cerrar">
        <span class="material-symbols-outlined">close</span>
      </button>
    </template>

    <form class="db-form" novalidate @submit.prevent="guardar">
      <div class="db-form-row">
        <div class="db-field">
          <label class="db-label" for="mu-nombre">Nombre</label>
          <input id="mu-nombre" v-model.trim="nombre" type="text" class="db-input" maxlength="100" autocomplete="off" />
        </div>
        <div class="db-field">
          <label class="db-label" for="mu-email">Correo electrónico</label>
          <input id="mu-email" v-model.trim="email" type="email" class="db-input" maxlength="191" autocomplete="off" />
          <p class="db-hint">Es el usuario con el que inicia sesión.</p>
        </div>
      </div>

      <div class="db-field">
        <label class="db-label" for="mu-rol">Rol</label>
        <select id="mu-rol" v-model="rol" class="db-input" :disabled="!puedeCambiarAcceso">
          <option value="" disabled>Elige un rol…</option>
          <option v-for="r in roles" :key="r.name" :value="r.name" :disabled="!r.acciones.asignar">
            {{ r.etiqueta }}{{ r.acciones.asignar ? "" : " (no puedes asignarlo)" }}
          </option>
        </select>
        <p v-if="usuario && usuario.es_yo" class="db-hint">No puedes cambiar tu propio rol.</p>
        <p v-else-if="rolSeleccionado" class="db-hint">{{ rolSeleccionado.descripcion || "Rol personalizado." }}</p>
        <ul v-if="rolSeleccionado && rolSeleccionado.permisos.length" class="mu-permisos">
          <li v-for="p in rolSeleccionado.permisos" :key="p" class="db-badge db-badge--info">{{ nombrePermiso(p) }}</li>
        </ul>
      </div>

      <section class="mu-box">
        <div class="mu-box-head">
          <h3 class="mu-box-title">Contraseña</h3>
          <label v-if="usuario" class="db-switch">
            <input v-model="restablecer" type="checkbox" @change="alCambiarRestablecer" />
            <span class="db-switch-track"></span>
            Restablecer contraseña
          </label>
        </div>

        <template v-if="!usuario || restablecer">
          <div class="db-field">
            <label class="db-label" for="mu-password">{{ usuario ? "Nueva contraseña" : "Contraseña inicial" }}</label>
            <div class="db-input-group">
              <input
                id="mu-password"
                v-model="password"
                type="text"
                class="db-input mu-password"
                maxlength="128"
                autocomplete="new-password"
                spellcheck="false"
              />
              <button type="button" class="db-icon-btn db-icon-btn--bordered" title="Generar otra" aria-label="Generar otra contraseña" @click="nuevaPassword">
                <span class="material-symbols-outlined">autorenew</span>
              </button>
              <button
                type="button"
                class="db-icon-btn db-icon-btn--bordered"
                :disabled="!password"
                :title="copiado ? 'Copiada' : 'Copiar'"
                :aria-label="copiado ? 'Contraseña copiada' : 'Copiar contraseña'"
                @click="copiar"
              >
                <span class="material-symbols-outlined">{{ copiado ? "check" : "content_copy" }}</span>
              </button>
            </div>
            <requisitos-password :password="password" />
          </div>

          <label class="db-switch">
            <input v-model="debeCambiar" type="checkbox" />
            <span class="db-switch-track"></span>
            Obligar a cambiarla en el próximo inicio de sesión
          </label>
        </template>
        <p v-else class="db-hint">La contraseña actual no se modificará.</p>
      </section>

      <section v-if="usuario" class="mu-box">
        <h3 class="mu-box-title">Acceso</h3>
        <div class="mu-grid">
          <div>
            <span class="db-hint">Intentos fallidos</span>
            <strong>{{ usuario.intentos_fallidos || 0 }}</strong>
          </div>
          <div>
            <span class="db-hint">Última IP</span>
            <strong>{{ usuario.ultima_ip || "—" }}</strong>
          </div>
          <div>
            <span class="db-hint">Estado</span>
            <span class="db-badge" :class="bloqueado ? 'db-badge--danger' : 'db-badge--success'">
              {{ bloqueado ? "Bloqueado" : "Activo" }}
            </span>
          </div>
          <label class="db-switch" :class="{ 'is-disabled': !puedeCambiarAcceso }">
            <input v-model="bloqueado" type="checkbox" :disabled="!puedeCambiarAcceso" />
            <span class="db-switch-track"></span>
            Bloquear acceso
          </label>
        </div>
        <p v-if="usuario.bloqueado_hasta && bloqueado" class="db-hint">
          Bloqueado automáticamente por intentos fallidos hasta {{ formatFechaHora(usuario.bloqueado_hasta) }}.
        </p>
        <p v-if="usuario.es_yo" class="db-hint">No puedes bloquear tu propia cuenta.</p>
      </section>
    </form>

    <template #modal-footer>
      <button type="button" class="db-btn db-btn--ghost" @click="cerrar">Cancelar</button>
      <button type="button" class="db-btn" :disabled="guardando" @click="guardar">
        <span v-if="guardando" class="db-spinner"></span>
        {{ usuario ? "Guardar cambios" : "Crear usuario" }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import axios from "@/directives/axios.js";
import RequisitosPassword from "@/components/dashboard/usuarios/RequisitosPassword.vue";
import {
  avisar,
  formatFecha,
  formatFechaHora,
  generarPassword,
  passwordValida,
  avisarError,
} from "@/directives/dbDialogs.js";

export default {
  name: "ModalUsuario",

  components: { RequisitosPassword },

  props: {
    modalId: { type: String, required: true },
    usuario: { type: Object, default: null },
    roles: { type: Array, default: () => [] },
    permisos: { type: Array, default: () => [] },
  },

  data() {
    return {
      nombre: "",
      email: "",
      rol: "",
      password: "",
      restablecer: false,
      debeCambiar: true,
      bloqueado: false,
      guardando: false,
      copiado: false,
    };
  },

  computed: {
    puedeCambiarAcceso() {
      return !this.usuario || Boolean(this.usuario.acciones && this.usuario.acciones.cambiar_acceso);
    },

    rolSeleccionado() {
      return this.roles.find((r) => r.name === this.rol) || null;
    },
  },

  methods: {
    formatFecha,
    formatFechaHora,

    nombrePermiso(name) {
      const p = this.permisos.find((x) => x.name === name);
      return p ? p.nombre : name;
    },

    reiniciar() {
      Object.assign(this.$data, this.$options.data.call(this));
    },

    alAbrir() {
      this.reiniciar();

      if (this.usuario) {
        this.nombre = this.usuario.name || "";
        this.email = this.usuario.email || "";
        this.rol = this.usuario.rol || "";
        this.bloqueado = Boolean(this.usuario.bloqueado);
      } else {
        this.nuevaPassword();
      }
    },

    alCambiarRestablecer() {
      if (this.restablecer && !this.password) this.nuevaPassword();
      if (!this.restablecer) this.password = "";
    },

    nuevaPassword() {
      this.password = generarPassword();
      this.copiado = false;
    },

    async copiar() {
      try {
        await navigator.clipboard.writeText(this.password);
        this.copiado = true;
        setTimeout(() => (this.copiado = false), 2000);
      } catch (e) {
        avisar(this, "No se pudo copiar. Selecciona la contraseña y cópiala a mano.", "warning");
      }
    },

    validar() {
      if (!this.nombre || !this.email) return "Nombre y correo son obligatorios.";
      if (!this.rol) return "Elige un rol.";
      if ((!this.usuario || this.restablecer) && !passwordValida(this.password)) {
        return "La contraseña no cumple los requisitos.";
      }
      return "";
    },

    async guardar() {
      const problema = this.validar();
      if (problema) {
        avisar(this, problema, "warning");
        return;
      }

      const payload = {
        name: this.nombre,
        email: this.email,
      };

      // Rol y bloqueo solo se envían si cambian (y nunca sobre la propia cuenta).
      if (this.puedeCambiarAcceso) {
        if (!this.usuario || this.rol !== this.usuario.rol) payload.roles = [this.rol];
        if (this.usuario && this.bloqueado !== Boolean(this.usuario.bloqueado)) payload.bloqueado = this.bloqueado;
      }

      if (!this.usuario || this.restablecer) {
        payload.password = this.password;
        payload.password_confirmation = this.password;
        payload.debe_cambiar_password = this.debeCambiar;
      }

      this.guardando = true;
      try {
        const response = this.usuario
          ? await axios.put(`/usuarios/${this.usuario.id}`, payload)
          : await axios.post("/usuarios", payload);

        const nombre = (response.data.usuario && response.data.usuario.name) || this.nombre;
        if (this.usuario) {
          const extra = [];
          if (payload.roles) extra.push("rol cambiado");
          if (payload.password) extra.push("contraseña restablecida");
          if ("bloqueado" in payload) extra.push(payload.bloqueado ? "acceso bloqueado" : "acceso desbloqueado");
          avisar(this, `Se han guardado los cambios de «${nombre}»${extra.length ? ` (${extra.join(", ")})` : ""}.`, "success", "Usuario modificado");
        } else {
          avisar(this, `«${nombre}» ya puede entrar en el panel con la contraseña inicial.`, "success", "Usuario creado");
        }
        // Si se ha editado la propia cuenta, se refrescan nombre y permisos del panel
        if (this.usuario && this.usuario.es_yo) this.$store.dispatch("cargarUsuario").catch(() => {});
        this.$emit("guardado", response.data.usuario);
        this.cerrar();
      } catch (error) {
        avisarError(this, error, "No se pudo guardar el usuario.");
      } finally {
        this.guardando = false;
      }
    },

    cerrar() {
      this.$bvModal.hide(this.modalId);
    },
  },
};
</script>

<style scoped>
.mu-password {
  font-family: ui-monospace, "Cascadia Code", Consolas, monospace;
  letter-spacing: 0.02em;
}

.mu-permisos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.mu-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--sd-border);
  border-radius: 12px;
  background: var(--sd-surface-2);
}

.mu-box-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mu-box-title {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sd-muted);
}

.mu-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
}

.mu-grid > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mu-grid strong {
  font-size: 1rem;
  color: var(--sd-text);
}

.db-switch.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
